import { useCallback, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { formatDate } from '../utils/voucherDates';

function buildVoucherShareText(voucher) {
  return [
    `🎪 MUNDOLANDIA PARK`,
    ``,
    `Bono: ${voucher.voucherCode}`,
    `Titular: ${voucher.customerName}`,
    `Teléfono: ${voucher.phone || 'No disponible'}`,
    `Correo: ${voucher.email || 'No disponible'}`,
    `Horas disponibles: ${voucher.hoursAvailable}h`,
    `Horas compradas: ${voucher.hoursPurchased}h`,
    `Caduca: ${formatDate(voucher.expiresAt)}`,
    ``,
    `Condiciones:`,
    `- 1 bono por persona`,
    `- Caducidad de 2 años`,
  ].join('\n');
}

function buildWhatsAppHref(voucher) {
  if (!voucher?.phone) {
    return null;
  }

  const normalizedPhone = voucher.phone.replace(/[^\d+]/g, '');
  const message = encodeURIComponent(
    `Hola ${voucher.customerName}, aquí tienes tu bono de Mundolandia Park.\n\n${buildVoucherShareText(voucher)}`
  );

  return `https://wa.me/${normalizedPhone}?text=${message}`;
}

function buildMailtoHref(voucher) {
  if (!voucher?.email) {
    return null;
  }

  return `mailto:${voucher.email}?subject=${encodeURIComponent(
    `Tu bono Mundolandia - ${voucher.voucherCode}`
  )}&body=${encodeURIComponent(
    `Hola ${voucher.customerName},\n\nAquí tienes los datos de tu bono de Mundolandia Park.\n\n${buildVoucherShareText(voucher)}`
  )}`;
}

export function useVoucherShare() {
  const cardRef = useRef(null);
  const [copyState, setCopyState] = useState('idle');
  const [capturing, setCapturing] = useState(false);

  const copyText = useCallback(async (voucher) => {
    try {
      await navigator.clipboard.writeText(buildVoucherShareText(voucher));
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 2000);
    } catch (_error) {
      setCopyState('error');
      window.setTimeout(() => setCopyState('idle'), 2000);
    }
  }, []);

  const captureCardAsImage = useCallback(async (voucher) => {
    if (!cardRef.current) {
      return;
    }

    setCapturing(true);
    const cardElement = cardRef.current;
    const previousWidth = cardElement.style.width;
    const previousMinWidth = cardElement.style.minWidth;
    const previousMaxWidth = cardElement.style.maxWidth;

    try {
      const exportWidth = 1400;

      cardElement.style.width = `${exportWidth}px`;
      cardElement.style.minWidth = `${exportWidth}px`;
      cardElement.style.maxWidth = `${exportWidth}px`;

      await new Promise((resolve) => window.requestAnimationFrame(resolve));

      const canvas = await html2canvas(cardElement, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        width: exportWidth,
        windowWidth: exportWidth,
      });

      const link = document.createElement('a');
      link.download = `bono-${voucher.voucherCode}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      throw error;
    } finally {
      cardElement.style.width = previousWidth;
      cardElement.style.minWidth = previousMinWidth;
      cardElement.style.maxWidth = previousMaxWidth;
      setCapturing(false);
    }
  }, []);

  return {
    cardRef,
    copyState,
    capturing,
    copyText,
    captureCardAsImage,
    buildWhatsAppHref,
    buildMailtoHref,
    buildVoucherShareText,
  };
}
